import { useEffect, useState } from 'react';
import type { ColetaAoVivo, ColetaEstado } from '../../api/types';
import { numero, t, umaCasa } from '../../i18n/idioma';
import { nomeDoItem } from '../../i18n/nomes';
import { GumpWindow, type WindowAnchor } from '../GumpWindow';
import { ImgDoItem } from '../ItemIcon';
import { tempoDesde } from './useTempoCacada';
import { recursoDe } from './coletaRecurso';
import { PilulaIdle } from './PilulaIdle';

const RAIO = 27;
const VOLTA = 2 * Math.PI * RAIO;
/** A fase (0 ida … 4 volta) → chave; andando e cortando dependem do recurso. */
function faseDe(fase: number, rec: ReturnType<typeof recursoDe>): string {
  return ['coleta.fase.ida', rec.t.faseAndando, rec.t.faseCortando, 'coleta.fase.parado', 'coleta.fase.volta'][fase]
    ?? rec.t.faseCortando;
}

/** A arte da ferramenta (o anel, a pílula e o Viajar): a de ouro vem tingida. */
export function ArteDaFerramenta({ id, size }: { id: string; size: number }) {
  return <ImgDoItem id={id} size={size} />;
}

/** A arte da pilha: o lingote e a tora de 1 são miúdos demais na janela. */
const PILHA = 25;
const pct = (p: number) => (p <= 0 ? '0%' : p < 0.01 ? '<1%' : `${Math.round(p * 100)}%`);
const um = (n: number) => umaCasa(Math.round(n * 10) / 10);

/**
 * A janela da coleta (techspec-coleta §07; protótipo `docs/prototipos/coleta-prototipo.html`): o anel da
 * tentativa, a régua da skill, a ferramenta, o cofre, o que saiu nesta coleta e o Cancelar. Minimiza
 * para a pílula; no celular abre como faixa no topo. A mineração usa a mesma janela, com os textos do
 * minério (techspec-mineracao §07).
 */
export function ColetaGump({
  anchor, vivo, estado, minimizado, travadoEm, cancelando, onMinimizar, onCancelar
}: {
  anchor: WindowAnchor;
  vivo: ColetaAoVivo;
  estado: ColetaEstado | null;
  minimizado: boolean;
  travadoEm: number;
  cancelando: boolean;
  onMinimizar(min: boolean): void;
  onCancelar(): void;
}) {
  const [agora, setAgora] = useState(() => Date.now());
  const [faixa, setFaixa] = useState(true);
  useEffect(() => {
    const id = window.setInterval(() => setAgora(Date.now()), 100);
    return () => window.clearInterval(id);
  }, []);

  const rec = recursoDe(vivo.recurso || estado?.ordem?.recurso);
  const lugar = estado?.lugares.find(l => l.id === rec.id) ?? null;
  const ferramenta = vivo.ferramenta || rec.ferramentaPadrao;
  const inicio = estado?.ordem ? Date.parse(estado.ordem.startedAt) : vivo.recebidoEm;
  const relogio = Number.isFinite(inicio) ? tempoDesde(inicio, agora).padStart(8, '00:') : '--:--';

  if (minimizado) {
    // A coleta não pausa (o veio e a árvore são do mundo): a pílula só tem o sair (techspec-hud-v3 §18).
    return (
      <PilulaIdle
        className="cl-min"
        arte={<ArteDaFerramenta id={ferramenta} size={22} />}
        estado={t(faseDe(vivo.fase, rec))}
        relogio={relogio}
        ocupado={cancelando}
        abrirDica={t('coleta.abrir')}
        sairDica={t('idle.sairColeta')}
        onAbrir={() => onMinimizar(false)}
        onSair={onCancelar}
      />
    );
  }

  const parado = vivo.fase === 3;
  const cortando = vivo.fase === 2;
  const prog = cortando && vivo.proximaEm > 0 && vivo.tentativaMs > 0
    ? Math.min(1, Math.max(0, 1 - (vivo.proximaEm - agora) / vivo.tentativaMs))
    : parado ? 1 : 0;
  const recente = agora - vivo.recebidoEm < 900;
  const anelCls = parado ? 'is-parado' : recente && vivo.ultimo === 1 ? 'is-ok' : recente && vivo.ultimo === 2 ? 'is-erro' : '';

  const chances = lugar?.chances ?? [];
  const falha = lugar?.falha ?? 0;
  // A machadinha de ouro soma no que sai (as chances já vêm com ela); a régua mostra a skill de verdade.
  const efetiva = vivo.skill + vivo.bonus;
  const prox = chances.find(c => c.skillMin > efetiva);
  const total = Object.values(vivo.toras).reduce((a, b) => a + b, 0);
  const usadas = estado?.cofreUsadas ?? 0;
  const vagas = estado?.cofreVagas ?? 50;
  const cofrePct = vagas ? usadas / vagas : 0;
  const lenhadores = lugar ? Math.max(0, lugar.lotacao - lugar.vagas) : null;
  const travado = travadoEm > 0 && agora - travadoEm < 3000;

  return (
    <GumpWindow
      title={t(rec.t.titulo)}
      anchor={anchor}
      className={`pop cl win--coleta cl--${rec.id}${faixa ? ' is-faixa' : ''}`}
      posicao="coleta"
      onMinimize={() => onMinimizar(true)}
    >
      <div className="cl-lugar">
        <span className="cl-arv"><ImgDoItem id={rec.itemComum} size={40} qty={PILHA} /></span>
        <span>
          <b>{lugar?.nome ?? t(rec.t.lugar)}</b>
          <small>{vivo.arvore ? t(rec.t.ponto, { n: vivo.arvore }) : t(faseDe(vivo.fase, rec))}</small>
        </span>
        {lenhadores !== null && (
          <span className="cl-vagas">{lenhadores}/{lugar!.lotacao}<small>{t(rec.t.coletores)}</small></span>
        )}
      </div>

      <div className="cl-agora">
        <div className={`cl-anel${cortando ? ' is-batendo' : ''} ${anelCls}`}
          style={{ ['--cl-golpe' as string]: `${Math.max(250, vivo.tentativaMs / Math.max(1, estado?.regras.golpes ?? 4))}ms` }}>
          <svg className="cl-arco" viewBox="0 0 64 64">
            <circle className="cl-fundo" cx="32" cy="32" r={RAIO} />
            <circle className="cl-prog" cx="32" cy="32" r={RAIO} strokeDasharray={VOLTA} strokeDashoffset={VOLTA * (1 - prog)} />
          </svg>
          <span className="cl-machado"><ArteDaFerramenta id={ferramenta} size={36} /></span>
        </div>
        <div className={`cl-estado${parado ? ' is-parado' : ''}`}>
          <div className="ch-live__topo">
            <span className="ch-pulso"><i />{t(faseDe(vivo.fase, rec))}</span>
            <span className="ch-relogio" title={t('coleta.tempo')}>⏱ <span className="cl-rel">{relogio}</span></span>
          </div>
          <Resultado vivo={vivo} recente={recente} rec={rec} />
        </div>
      </div>

      {parado && <p className="cl-aviso">{t(vivo.parado === 'cofre' ? 'coleta.paradoCofre' : 'coleta.res.parada')}</p>}
      {travado && <p className="cl-trava">{t(rec.t.travado)}</p>}

      <button type="button" className="cl-mais" onClick={() => setFaixa(f => !f)}>{t(faixa ? 'coleta.verDetalhes' : 'coleta.menos')}</button>

      <div className="cl-skill">
        <div className="cl-skill__t">
          <span>{t(rec.t.skill)}</span>
          <span>{vivo.bonus > 0 && <em className="cl-bonus">+{numero(vivo.bonus)}</em>}<b>{um(vivo.skill)}</b></span>
        </div>
        <div className="cl-regua">
          <i style={{ width: `${Math.min(100, vivo.skill)}%` }} />
          {chances.filter(c => c.skillMin > 0).map(c => (
            <s key={c.tora} className={vivo.skill >= c.skillMin ? 'is-tem' : ''}
              style={{ left: `${c.skillMin}%`, ['--c' as string]: rec.cores[c.tora] ?? '#c9a227' }}
              title={`${nomeDoItem(c.tora)}: ${um(c.skillMin)}`} />
          ))}
        </div>
        <p className="cl-prox">
          {prox ? t(rec.t.proxima, { nome: nomeDoItem(prox.tora), skill: um(prox.skillMin) }) : t(rec.t.todas)}
        </p>
      </div>

      <div className="cl-gr2">
        <div className="ch-tile" title={t(rec.t.ferramentaDica)}>
          <ImgDoItem id={ferramenta} size={26} />
          <b>{nomeDoItem(ferramenta)}</b>
          <small>{vivo.bonus > 0 ? t(rec.t.bonus, { n: numero(vivo.bonus) }) : t('coleta.semBonus')}</small>
        </div>
        <div className={`ch-tile${cofrePct > 0.97 ? ' is-alerta' : ''}`} title={t(rec.t.cofreDica)}>
          <ImgDoItem id={rec.itemComum} size={26} qty={PILHA} />
          <b>{t('coleta.cofre')}</b>
          <small>
            <span className={`cl-barrinha${cofrePct > 0.97 ? ' is-zero' : cofrePct > 0.85 ? ' is-baixo' : ''}`}><i style={{ width: `${cofrePct * 100}%` }} /></span>
            {t('coleta.vagas', { usadas, vagas })}
          </small>
        </div>
      </div>

      <h5 className="cl-h">{t('coleta.nesta')} <small>{t(rec.t.itensChance, { n: total })}</small></h5>
      <div className={`cl-toras${chances.length > 7 ? ' is-muitos' : ''}`}>
        {chances.map(c => {
          const n = vivo.toras[c.tora] ?? 0;
          const sai = recente && vivo.ultimo === 1 && vivo.ultimaTora === c.tora ? ' is-sai' : '';
          return c.trancada
            ? (
              <span key={c.tora} className="cl-t is-tranca" title={t(rec.t.trancada, { nome: nomeDoItem(c.tora), skill: um(c.skillMin) })}>
                <ImgDoItem id={c.tora} size={26} qty={PILHA} /><b>—</b><small>{numero(c.skillMin)}</small>
              </span>
            )
            : (
              <span key={c.tora} className={`cl-t${n ? '' : ' is-zero'}${sai}`} title={t('coleta.chanceDica', { nome: nomeDoItem(c.tora), chance: pct(c.chance) })}>
                <ImgDoItem id={c.tora} size={26} qty={PILHA} /><b>{numero(n)}</b><small>{pct(c.chance)}</small>
              </span>
            );
        })}
      </div>
      <div className="cl-num">
        <span>{t('coleta.tentativas')} <b>{numero(vivo.tentativas)}</b></span>
        <span>{t('coleta.acerto')} <b>{vivo.tentativas ? Math.round((vivo.acertos / vivo.tentativas) * 100) : 0}%</b></span>
        <span>{t('coleta.erroAgora')} <b>{pct(falha)}</b></span>
      </div>

      <div className="ch-pe">
        <button type="button" className="btn btn--danger" disabled={cancelando || vivo.fase === 4} onClick={onCancelar}>
          {t(vivo.fase === 4 ? 'coleta.voltando' : 'coleta.cancelar')}
        </button>
        <small>{t('coleta.cancelarDica', { cidade: vivo.origem })}</small>
      </div>
    </GumpWindow>
  );
}

function Resultado({ vivo, recente, rec }: { vivo: ColetaAoVivo; recente: boolean; rec: ReturnType<typeof recursoDe> }) {
  const novo = recente ? ' is-novo' : '';
  if (vivo.fase === 0) return <div className="cl-res">{t(rec.t.resIda)}</div>;
  if (vivo.fase === 1) return <div className="cl-res">{vivo.arvore ? t(rec.t.resAndando, { n: vivo.arvore }) : t(rec.t.resProcurando)}</div>;
  if (vivo.fase === 4) return <div className="cl-res">{t('coleta.res.volta')}</div>;
  if (vivo.fase === 3) return <div className="cl-res is-erro">{t('coleta.res.parada')}</div>;
  if (vivo.ultimo === 0) return <div className="cl-res">{t('coleta.res.primeira', { s: Math.round(vivo.tentativaMs / 1000) })}</div>;
  if (vivo.ultimo === 1)
    return (
      <div className={`cl-res${novo}`}>
        <ImgDoItem id={vivo.ultimaTora || rec.itemComum} size={22} qty={PILHA} /><b>+{vivo.ultimaQtd}</b> {nomeDoItem(vivo.ultimaTora)} <small>{t('coleta.res.banco')}</small>
      </div>
    );
  return <div className={`cl-res is-erro${novo}`}>{t(rec.t.resErro)}</div>;
}
